import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";

const FeatureFlagsContext = createContext({
  flags: {},
  isLoading: true,
  isEnabled: () => false,
  getFlag: () => null,
  refresh: () => {},
  previewMode: false,
  setPreviewMode: () => {},
});

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider");
  }
  return context;
};

export const FeatureFlagsProvider = ({ children }) => {
  const { sessionToken, isAuthenticated, isSuperAdmin } = useAuth();
  const [flags, setFlags] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Preview mode - allows super admins to see all features while impersonating
  // Stored in sessionStorage so it persists during the session but not across tabs
  const [previewMode, setPreviewModeState] = useState(() => {
    return sessionStorage.getItem("feature_preview_mode") === "true";
  });

  const setPreviewMode = useCallback((enabled) => {
    setPreviewModeState(enabled);
    if (enabled) {
      sessionStorage.setItem("feature_preview_mode", "true");
    } else {
      sessionStorage.removeItem("feature_preview_mode");
    }
  }, []);

  // Listen for preview mode changes from the impersonation banner
  useEffect(() => {
    const handlePreviewChange = () => {
      setPreviewModeState(sessionStorage.getItem("feature_preview_mode") === "true");
    };
    window.addEventListener("feature-preview-changed", handlePreviewChange);
    return () => window.removeEventListener("feature-preview-changed", handlePreviewChange);
  }, []);

  // Get current tenant context (for impersonation)
  const currentTenantId = sessionStorage.getItem("viewing_tenant_id");
  const isImpersonating = !!localStorage.getItem("impersonation_token");

  const fetchFlags = useCallback(async () => {
    if (!sessionToken || !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (currentTenantId) {
        params["X-Tenant-ID"] = currentTenantId;
      }

      const response = await axios.get(`${API}/feature-flags/my-flags`, { params });
      setFlags(response.data.flags || {});
    } catch (error) {
      console.error("Error fetching feature flags:", error);
      // On error, default to empty flags (features hidden)
      setFlags({});
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken, isAuthenticated, currentTenantId]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Check if a feature is enabled
  const isEnabled = useCallback((featureId) => {
    // Super admins always see all features (when not impersonating)
    if (isSuperAdmin && !isImpersonating) return true;
    
    // Preview mode - super admins can see all features even while impersonating
    // This is for testing purposes only - doesn't affect the actual user
    if (previewMode && isSuperAdmin) return true;
    
    const flag = flags[featureId];
    if (!flag) return false;
    return flag.enabled === true;
  }, [flags, isSuperAdmin, isImpersonating, previewMode]);

  // Get full flag info (for UI display)
  const getFlag = useCallback((featureId) => {
    return flags[featureId] || null;
  }, [flags]);

  // Manual refresh (useful after admin changes)
  const refresh = useCallback(() => {
    setIsLoading(true);
    fetchFlags();
  }, [fetchFlags]);

  return (
    <FeatureFlagsContext.Provider value={{ 
      flags, 
      isLoading, 
      isEnabled, 
      getFlag, 
      refresh,
      previewMode,
      setPreviewMode,
      isImpersonating
    }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsContext;
